import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { Attachments } from "@/pages/admin/Fields";

const move = (arr, i, d) => {
  const a = [...arr];
  const j = i + d;
  if (j < 0 || j >= a.length) return a;
  [a[i], a[j]] = [a[j], a[i]];
  return a;
};

const QuizEditor = ({ value = [], onChange }) => (
  <div className="space-y-3">
    {value.map((q, i) => (
      <div key={i} className="border border-white/10 rounded p-3 space-y-2">
        <div className="flex gap-2">
          <input value={q.question} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)))} placeholder="Question" className="field !py-2" />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400" aria-label="Remove question"><Trash2 size={14} /></button>
        </div>
        <textarea rows={3} value={(q.options || []).join("\n")} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, options: e.target.value.split("\n") } : x)))} placeholder="Options, one per line" className="field !py-2" />
        <label className="text-xs text-slate-400 flex items-center gap-2">Correct option #
          <input type="number" min={1} value={(q.answer ?? 0) + 1} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, answer: Number(e.target.value) - 1 } : x)))} className="field !py-1 !w-20" />
        </label>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...value, { question: "", options: [], answer: 0 }])} className="text-xs text-gold-light flex items-center gap-1"><Plus size={12} />Add quiz question</button>
  </div>
);

const LessonEditor = ({ l, onChange, onRemove, onMove }) => {
  const set = (k) => (v) => onChange({ ...l, [k]: v });
  return (
    <div className="border border-white/10 rounded-lg p-4 space-y-3 bg-black/30">
      <div className="flex gap-2">
        <input value={l.title} onChange={(e) => set("title")(e.target.value)} placeholder="Lesson title" className="field !py-2" data-testid="lesson-title-input" />
        <input value={l.duration || ""} onChange={(e) => set("duration")(e.target.value)} placeholder="10:00" className="field !py-2 !w-24" />
        <button type="button" onClick={() => onMove(-1)} className="text-slate-400" aria-label="Move up"><ChevronUp size={16} /></button>
        <button type="button" onClick={() => onMove(1)} className="text-slate-400" aria-label="Move down"><ChevronDown size={16} /></button>
        <button type="button" onClick={onRemove} className="text-slate-400 hover:text-red-400" aria-label="Remove lesson"><Trash2 size={15} /></button>
      </div>
      <input value={l.video_url || ""} onChange={(e) => set("video_url")(e.target.value)} placeholder="Video URL (YouTube, Vimeo, MP4)" className="field !py-2" />
      <textarea rows={3} value={l.content || ""} onChange={(e) => set("content")(e.target.value)} placeholder="Lesson notes" className="field !py-2" />
      <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={!!l.free_preview} onChange={(e) => set("free_preview")(e.target.checked)} />Free preview lesson</label>
      <p className="label !mb-1">Lesson downloads</p>
      <Attachments id={`att-${l.id || "new"}`} value={l.attachments || []} onChange={set("attachments")} />
      <p className="label !mb-1">Quiz (optional)</p>
      <QuizEditor value={l.quiz || []} onChange={set("quiz")} />
    </div>
  );
};

export default function ModulesEditor({ value, onChange }) {
  const setMod = (i, m) => onChange(value.map((x, j) => (j === i ? m : x)));
  return (
    <div className="space-y-5" data-testid="modules-editor">
      {value.map((m, i) => (
        <div key={m.id || i} className="border border-[#D4AF37]/25 rounded-xl p-4 space-y-4">
          <div className="flex gap-2">
            <input value={m.title} onChange={(e) => setMod(i, { ...m, title: e.target.value })} placeholder="Module title" className="field" data-testid={`module-title-input-${i}`} />
            <button type="button" onClick={() => onChange(move(value, i, -1))} className="text-slate-400" aria-label="Move module up"><ChevronUp size={16} /></button>
            <button type="button" onClick={() => onChange(move(value, i, 1))} className="text-slate-400" aria-label="Move module down"><ChevronDown size={16} /></button>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400" aria-label="Remove module"><Trash2 size={16} /></button>
          </div>
          {(m.lessons || []).map((l, li) => (
            <LessonEditor key={l.id || li} l={l}
              onChange={(nl) => setMod(i, { ...m, lessons: m.lessons.map((x, j) => (j === li ? nl : x)) })}
              onRemove={() => setMod(i, { ...m, lessons: m.lessons.filter((_, j) => j !== li) })}
              onMove={(d) => setMod(i, { ...m, lessons: move(m.lessons, li, d) })} />
          ))}
          <button type="button" onClick={() => setMod(i, { ...m, lessons: [...(m.lessons || []), { title: "", video_url: "", attachments: [], quiz: [] }] })} className="text-sm text-gold-light flex items-center gap-1" data-testid={`add-lesson-btn-${i}`}><Plus size={14} />Add lesson</button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { title: "", lessons: [] }])} className="btn-outline !py-2" data-testid="add-module-btn"><Plus size={14} />Add module</button>
    </div>
  );
}
